export interface TelegramTheme {
  colorScheme?: 'light' | 'dark';
  themeParams?: Record<string, string | undefined>;
}

export type ThemePreference = 'system' | 'light' | 'dark';
export const THEME_STORAGE_KEY = 'cora.theme';
let memoryPreference: ThemePreference = 'system';

export function parseThemePreference(value: unknown): ThemePreference {
  return value === 'light' || value === 'dark' ? value : 'system';
}

export function getThemePreference(): ThemePreference {
  try { return parseThemePreference(localStorage.getItem(THEME_STORAGE_KEY)); }
  catch { return memoryPreference; }
}

export function saveThemePreference(value: ThemePreference) {
  memoryPreference = parseThemePreference(value);
  try { localStorage.setItem(THEME_STORAGE_KEY, memoryPreference); } catch { /* private mode */ }
  window.dispatchEvent(new Event('cora:theme-preference'));
}

/** Только валидные цвета; сиреневый фирменный акцент остаётся в обеих темах. */
export function themeVariables(telegram: TelegramTheme | undefined, systemDark: boolean, preference: ThemePreference = 'system') {
  const dark = preference === 'system' ? telegram?.colorScheme ? telegram.colorScheme === 'dark' : systemDark : preference === 'dark';
  const p = preference === 'system' ? telegram?.themeParams ?? {} : {};
  const color = (value: string | undefined, fallback: string) => /^#[0-9a-f]{6}$/i.test(value ?? '') ? value! : fallback;
  const bg = color(p.bg_color, dark ? '#17171e' : '#ffffff');
  const section = color(p.section_bg_color ?? p.secondary_bg_color, dark ? '#24242f' : '#f3f5ff');
  const text = color(p.text_color, dark ? '#f1f0f7' : '#2b2b2b');
  const rgb = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)).join(', ');
  return {
    scheme: dark ? 'dark' as const : 'light' as const,
    bg,
    vars: {
      '--bg': bg, '--mist': bg, '--secondary-bg': section,
      '--ink': text, '--text': text,
      '--hint': color(p.hint_color, dark ? '#a4a3ba' : '#77778f'),
      '--border': color(p.section_separator_color, dark ? '#393947' : '#e6e8f7'),
      '--danger': color(p.destructive_text_color, dark ? '#ff7b83' : '#d94a4a'),
      '--brand': dark ? '#aaa6ff' : '#8686e2',
      '--brand-deep': dark ? '#c1bdff' : '#6161ac',
      '--accent-text': dark ? '#17171e' : '#ffffff',
      '--macro-p': dark ? '#c1bdff' : '#6161ac',
      '--macro-f': dark ? '#b9d665' : '#8faf22',
      '--macro-c': dark ? '#aaa6ff' : '#8686e2',
      '--glass-rgb': rgb(dark ? section : bg),
      '--page-rgb': rgb(bg),
      '--glass-border': dark ? 'rgba(255,255,255,.12)' : 'rgba(255,255,255,.75)',
      '--glass-highlight': dark ? 'rgba(255,255,255,.07)' : 'rgba(255,255,255,.6)',
      '--glass-edge': dark ? 'rgba(255,255,255,.12)' : 'rgba(255,255,255,.95)',
    },
  };
}
